import { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { groupByCategory } from '@/modules/agents/utils/joePicks';
import type { Pick } from '../api/communityApi';
import styles from './ReservePicks.module.css';

/**
 * Joe's other ranked options for the month, folded away and labelled optional so they never read
 * as more picks: first the scan's own choices whose why line Joe has not written (hidden from the
 * picks until he does), then places and events it scored but did not choose, grouped by category.
 * "Add to my picks" turns one into a normal pick straight away. It is the owner's own action, so it
 * does not depend on Joe running or on him following a request in his task thread.
 */
export function ReservePicks({
  picks,
  busy,
  onAdd,
  step,
}: {
  picks: Pick[];
  busy: boolean;
  onAdd: (pick: Pick) => void;
  /** The step number shown beside the heading when this follows numbered steps. */
  step?: number;
}) {
  const [open, setOpen] = useState(false);
  if (picks.length === 0) return null;
  const groups = groupByCategory(picks);
  return (
    <section className={styles.panel} aria-label="Other options Joe ranked">
      <button type="button" className={styles.header} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {step !== undefined && (
          <span className={styles.step} aria-hidden="true">
            {step}
          </span>
        )}
        <span className={styles.headerText}>
          <span className={styles.title}>
            Want different people? <span className={styles.optional}>Optional</span>
          </span>
          <span className={styles.sub}>
            Joe ranked {picks.length} more nearby who didn&apos;t make this month&apos;s list. Add any of them to your picks.
          </span>
        </span>
        <ChevronDown size={18} aria-hidden="true" className={clsx(styles.chevron, open && styles.chevronOpen)} />
      </button>
      {open && (
        <div className={styles.body}>
          {groups.map((group) => (
            <div key={group.label} className={styles.group}>
              <div className={styles.groupTitle}>
                {group.label} · {group.items.length}
              </div>
              <ul className={styles.list}>
                {group.items.map((pick) => (
                  <li key={pick.id} className={styles.row}>
                    <div className={styles.text}>
                      <span className={styles.name}>
                        {pick.name} <span className={styles.meta}>· {pick.distanceMi.toFixed(1)} mi</span>
                      </span>
                      <span className={styles.fact}>{pick.why}</span>
                    </div>
                    <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAdd(pick)}>
                      Add to my picks
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
